import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ShieldAlert, ShieldX, Loader2, Lock, Sparkles, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";
import { useSeo } from "../lib/useSeo";
import { useAuth } from "../context/AuthContext";
import { trackEvent } from "../lib/analytics";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

const riskStyle = {
  laag: { icon: ShieldCheck, bg: "bg-success/10", ring: "border-success/30", text: "text-success" },
  gemiddeld: { icon: ShieldAlert, bg: "bg-warning/10", ring: "border-warning/30", text: "text-warning" },
  hoog: { icon: ShieldX, bg: "bg-danger/10", ring: "border-danger/30", text: "text-danger" },
};

const ScamCheck = () => {
  const { t } = useT();
  useSeo({ title: t("seo.scam.title"), description: t("seo.scam.desc"), image: "/og-image.png", path: "/scam-check" });
  const { user } = useAuth();
  const [form, setForm] = useState({ url: "", price: "", location: "", description: "" });
  const [quota, setQuota] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) api("/scam-check/quota").then(setQuota).catch(() => {});
  }, [user]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setResult(null);
    setLoading(true);
    try {
      const res = await api("/scam-check", {
        method: "POST",
        body: JSON.stringify({
          url: form.url || null,
          price: Number(form.price) || 0,
          location: form.location || null,
          description: form.description,
        }),
      });
      setResult(res);
      api("/scam-check/quota").then(setQuota).catch(() => {});
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="mx-auto max-w-md px-4 py-24 text-center">
        <Lock className="mx-auto text-slate-300" size={44} />
        <h1 className="mt-4 font-display text-2xl font-bold text-navy">{t("scam.title")}</h1>
        <p className="mt-2 text-slate-500">{t("scam.loginRequired")}</p>
        <Button asChild className="mt-6 rounded-xl bg-brandblue hover:bg-brandblue/90">
          <Link to="/login" data-testid="scam-login-link">{t("nav.login")}</Link>
        </Button>
      </div>
    );
  }

  const rs = result ? riskStyle[result.risk_level] || riskStyle.gemiddeld : null;

  return (
    <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6">
      <div className="text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-brandblue-light text-brandblue">
          <ShieldCheck size={28} />
        </div>
        <h1 className="mt-4 font-display text-3xl font-extrabold text-navy">{t("scam.title")}</h1>
        <p className="mt-2 text-slate-500">{t("scam.subtitle")}</p>
        {quota && (
          <p className="mt-2 text-sm font-medium text-brandblue" data-testid="scam-quota">
            {quota.limit === null ? t("scam.quotaUnlimited") : t("scam.quota", { n: quota.remaining })}
          </p>
        )}
      </div>

      <form onSubmit={submit} className="mt-8 space-y-4 rounded-3xl border border-border bg-white p-6 shadow-sm">
        <div>
          <Label className="text-sm font-semibold text-navy">{t("scam.url")}</Label>
          <Input className="mt-2 rounded-xl" data-testid="scam-url" value={form.url} onChange={(e) => set("url", e.target.value)} placeholder="https://…" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label className="text-sm font-semibold text-navy">{t("scam.price")}</Label>
            <Input
              className="mt-2 rounded-xl"
              data-testid="scam-price"
              type="number"
              inputMode="numeric"
              value={form.price}
              onChange={(e) => set("price", e.target.value)}
              placeholder="1250"
            />
          </div>
          <div>
            <Label className="text-sm font-semibold text-navy">{t("scam.location")}</Label>
            <Input className="mt-2 rounded-xl" data-testid="scam-location" value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="Amsterdam" />
          </div>
        </div>
        <div>
          <Label className="text-sm font-semibold text-navy">{t("scam.description")}</Label>
          <Textarea
            className="mt-2 min-h-[140px] rounded-xl"
            data-testid="scam-description"
            required
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
            placeholder="Plak hier de volledige advertentietekst…"
          />
        </div>
        {error && <p className="rounded-xl bg-danger/10 px-4 py-3 text-sm text-danger" data-testid="scam-error">{error}</p>}
        <Button type="submit" disabled={loading} data-testid="scam-submit" className="w-full rounded-xl bg-brandblue hover:bg-brandblue/90">
          {loading ? (
            <>
              <Loader2 className="mr-2 animate-spin" size={18} /> {t("scam.analyzing")}
            </>
          ) : (
            t("scam.submit")
          )}
        </Button>
      </form>

      {result && rs && (
        <div className={`mt-6 rounded-3xl border ${rs.ring} ${rs.bg} p-6`} data-testid="scam-result">
          <div className="flex items-center gap-3">
            <rs.icon className={rs.text} size={32} />
            <div>
              <div className={`font-display text-xl font-extrabold ${rs.text}`}>{t(`scam.risk.${result.risk_level}`)}</div>
              <div className="text-sm text-slate-500">Score: {result.score}/100</div>
            </div>
          </div>
          <p className="mt-4 text-slate-700">{result.summary}</p>

          {result.red_flags?.length > 0 && (
            <div className="mt-5">
              <h3 className="font-display text-sm font-bold text-navy">{t("scam.redflags")}</h3>
              <ul className="mt-2 space-y-1.5">
                {result.red_flags.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-danger" /> {f}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {result.recommendations?.length > 0 && (
            <div className="mt-5">
              <h3 className="font-display text-sm font-bold text-navy">{t("scam.recommendations")}</h3>
              <ul className="mt-2 space-y-1.5">
                {result.recommendations.map((r, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-success" /> {r}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {result && !user?.is_premium && (
        <ScamPremiumNudge riskLevel={result.risk_level} score={result.score} t={t} />
      )}
    </div>
  );
};

const ScamPremiumNudge = ({ riskLevel, score, t }) => {
  useEffect(() => {
    trackEvent("scam_nudge_viewed", { risk_level: riskLevel, score });
  }, [riskLevel, score]);
  const onCta = () => trackEvent("scam_nudge_click", { risk_level: riskLevel, target: "premium" });
  return (
    <div
      data-testid="scam-premium-nudge"
      className="mt-6 rounded-3xl border border-amber-300/60 bg-gradient-to-br from-amber-50/50 via-white to-white p-6 shadow-sm"
    >
      <div className="flex items-start gap-4">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-navy text-amber-300 ring-1 ring-amber-300/40">
          <Sparkles size={20} />
        </span>
        <div className="flex-1">
          <h3 className="font-display text-lg font-extrabold text-navy">{t("scam.nudge.title")}</h3>
          <p className="mt-1 text-sm text-slate-600">{t("scam.nudge.body")}</p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button
              asChild
              className="rounded-full bg-navy text-amber-300 hover:bg-navy/90"
              data-testid="scam-nudge-cta"
              onClick={onCta}
            >
              <Link to="/premium">{t("scam.nudge.cta")} <ArrowRight size={14} className="ml-1" /></Link>
            </Button>
            <Link
              to="/prijzen"
              onClick={() => trackEvent("scam_nudge_click", { risk_level: riskLevel, target: "prijzen" })}
              className="text-sm font-semibold text-amber-700 hover:text-amber-800"
              data-testid="scam-nudge-prijzen"
            >
              {t("scam.nudge.pricing")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScamCheck;
